import { useState, useEffect, useCallback } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import EditNoteSharpIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import axiosInstance from "../../../Config/axiosInstance";
import AddInvoice from "./CreateInvoice";
import EditInvoice from "./EditInvoices";
import DetailsInvoice from "./DetailsInvoice";
import NotAuthorized from "../../../Components/ProtectedRoute/NotAuthorized";
import InvoiceDeleteDialog from "./InvoiceDeleteDialog";
import "./InvoicesRegister.css";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from "@mui/material";

import { format, parseISO, isValid } from "date-fns";

function InvoicesRegister() {
  const [canCreate, setCanCreate] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canView, setCanView] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [rows, setRows] = useState([]);
  const [systemCurrency, setSystemCurrency] = useState("");

  const navigate = useNavigate();
  const location = useLocation();

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [deleteErrorMessage, setDeleteErrorMessage] = useState("");

  const formatDate = (value) => {
    if (!value) return "";
    try {
      const date = parseISO(value);
      return isValid(date) ? format(date, "dd.MM.yyyy") : value;
    } catch {
      return value;
    }
  };

  const formatNumberRO = (value) => {
    if (value === null || value === undefined || value === "") return "-";

    const raw = String(value).trim();
    const match = raw.match(/-?[\d.,\s]+/);
    if (!match) return raw;

    const numericPart = match[0].trim();
    let normalized = numericPart.replace(/\s/g, "");

    if (normalized.includes(",") && normalized.includes(".")) {
      normalized = normalized.replace(/\./g, "").replace(",", ".");
    } else if (normalized.includes(",")) {
      normalized = normalized.replace(",", ".");
    }

    const n = Number(normalized);
    if (Number.isNaN(n)) return raw;

    const formatted = new Intl.NumberFormat("ro-RO", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(n);

    const suffix = raw.replace(match[0], "").trim();
    return suffix ? `${formatted} ${suffix}` : formatted;
  };

  const getProjectCodesFromLines = (lines) => {
    if (!Array.isArray(lines) || lines.length === 0) return "-";

    const codes = [
      ...new Set(
        lines
          .map((line) => line?.project_code_label)
          .filter(Boolean)
      )
    ];

    return codes.length ? codes.join(", ") : "-";
  };

  const getSubprojectCodesFromLines = (lines) => {
    if (!Array.isArray(lines) || lines.length === 0) return "-";

    const codes = [
      ...new Set(
        lines
          .map((line) => line?.subproject_code_label)
          .filter(Boolean)
      )
    ];

    return codes.length ? codes.join(", ") : "-";
  };

  const fetchSystemCurrency = useCallback(async () => {
    try {
      const response = await axiosInstance.get("/system-currency/1/");
      setSystemCurrency(response.data?.currency_label || "");
    } catch (error) {
      console.error("Error fetching system currency:", error);
    }
  }, []);

  useEffect(() => {
    const requiredPath = "/invoices_register";
    const storedPermissions = JSON.parse(localStorage.getItem("permissions"));
    const pages = storedPermissions?.pages || [];

    const matchPage = (page) => {
      try {
        return new URL(page.url, window.location.origin).pathname === requiredPath;
      } catch {
        return page.url === requiredPath;
      }
    };

    setCanCreate(pages.some((p) => matchPage(p) && p.can_create === true));
    setCanEdit(pages.some((p) => matchPage(p) && p.can_edit === true));
    setCanView(pages.some((p) => matchPage(p) && p.can_view === true));
    setCanDelete(pages.some((p) => matchPage(p) && p.can_delete === true));
  }, []);

  const fetchInvoices = useCallback(async () => {
    try {
      const res = await axiosInstance.get("/invoices-register/");

      const sortedData = (res.data || [])
        .map((item) => ({
          id: item.id,
          invoice_number: item.invoice_number || "-",
          contract: item.contract_label || "-",
          addendum: item.addendum_label || "-",  // <-- ADĂUGAT
          client: item.client_label || "-",
          project_code: getProjectCodesFromLines(item.invoice_lines_data || item.invoice_lines),
          subproject_code: getSubprojectCodesFromLines(item.invoice_lines_data || item.invoice_lines),
          project_responsible: item.project_responsible_label || "-",
          contract_paper_date: item.contract_paper_date,
          invoice_date: item.invoice_date,
          invoice_due_date: item.invoice_due_date,
          receipt_due_date: item.receipt_due_date,
          currency: item.currency_label || "-",
          total_without_vat: item.total_without_vat_display || "-",
          total_with_vat: item.total_with_vat_display || "-",
          total_without_vat_system: item.total_without_vat_system_display || "-",
          total_with_vat_system: item.total_with_vat_system_display || "-",
          total_paid: item.total_paid || "-",
          remaining_payment: item.remaining_payment_display || "-",
          remaining_payment_system: item.remaining_payment_system_display || "-",
          payment: item.payment || "-",
          status: item.status || "-",
          description: item.description || "-",
        }))
        .sort((a, b) => b.id - a.id);

      setRows(sortedData);
    } catch (error) {
      console.error("Eroare la obținerea facturilor:", error.response?.data || error.message);
      setDeleteErrorMessage(error.response?.data?.message || error.message);
      setErrorDialogOpen(true);
    }
  }, []);

  useEffect(() => {
    fetchInvoices();
    fetchSystemCurrency();
  }, [location.pathname, fetchInvoices, fetchSystemCurrency]);

  const handleAddClick = () => navigate("/invoices_register/create");

  const handleEditClick = (row) => navigate(`/invoices_register/edit/${row.id}`);

  const handleViewClick = (row) =>
    navigate(`/invoices_register/details-payment-invoice-commercial/${row.id}`);

  const handleDeleteClick = (row) => {
    setItemToDelete(row);
    setOpenDeleteDialog(true);
  };

  const columns = [
    {
      field: "invoice_number",
      headerName: "Invoice Series and Number",
      flex: 1.3,
      minWidth: 135,
      renderCell: (params) => (
        <div className="cell-wrap">{params.value}</div>
      )
    },
    {
      field: "contract",
      headerName: "Contract Number",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <div className="cell-wrap">{params.value}</div>
      )
    },
    {
      field: "addendum",  // <-- NOUA COLOANĂ
      headerName: "Addendum Number",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <div className="cell-wrap">{params.value || "-"}</div>
      )
    },
    {
      field: "project_code",
      headerName: "Project Code",
      flex: 1.3,
      minWidth: 220,
      renderCell: (params) => (
        <div className="cell-wrap">{params.value}</div>
      )
    },
    {
      field: "client",
      headerName: "Client",
      flex: 1.4,
      minWidth: 220,
      renderCell: (params) => (
        <div className="cell-wrap">{params.value}</div>
      )
    },
    {
      field: "project_responsible",
      headerName: "Project Responsible",
      flex: 1.2,
      minWidth: 200,
      renderCell: (params) => (
        <div className="cell-wrap">{params.value}</div>
      )
    },
    {
      field: "contract_paper_date",
      headerName: "Contract / Addendum Date",
      flex: 1,
      minWidth: 180,
      renderCell: (params) => (
        <div className="cell-wrap">{formatDate(params.value)}</div>
      )
    },
    {
      field: "invoice_date",
      headerName: "Invoice Date",
      flex: 0.9,
      minWidth: 130,
      renderCell: (params) => (
        <div className="cell-wrap">{formatDate(params.value)}</div>
      )
    },
    {
      field: "total_with_vat",
      headerName: "Total with VAT",
      flex: 1,
      minWidth: 150,
      sortable: false,
      renderCell: (params) => (
        <div className="cell-wrap amount-cell">{formatNumberRO(params.value)}</div>
      )
    },
    {
      field: "total_without_vat",
      headerName: "Total without VAT",
      flex: 1,
      minWidth: 150,
      sortable: false,
      renderCell: (params) => (
        <div className="cell-wrap amount-cell">{formatNumberRO(params.value)}</div>
      )
    },
    {
      field: "total_without_vat_system",
      headerName: "Total System without VAT",
      flex: 1.2,
      minWidth: 180,
      sortable: false,
      renderCell: (params) => (
        <div className="cell-wrap amount-cell">{formatNumberRO(params.value)}</div>
      )
    },
    {
      field: "total_with_vat_system",
      headerName: "Total System with VAT",
      flex: 1.2,
      minWidth: 170,
      sortable: false,
      renderCell: (params) => (
        <div className="cell-wrap amount-cell">{formatNumberRO(params.value)}</div>
      )
    },
    {
      field: "payment",
      headerName: "Payment Status",
      flex: 1,
      minWidth: 170,
      renderCell: (params) => {
        const value = params.value?.trim() || "";
        let className = "payment-status-badge ";

        if (value === "Unsettled") className += "payment-unpaid";
        else if (value === "Fully settled") className += "payment-full";
        else if (value === "Partially settled") className += "payment-partial";

        return <span className={className}>{value || "-"}</span>;
      }
    },
    {
      field: "remaining_payment",
      headerName: "Remaining",
      flex: 1.1,
      minWidth: 180,
      sortable: false,
      renderCell: (params) => (
        <div className="cell-wrap amount-cell">
          {formatNumberRO(params.row.remaining_payment)}
        </div>
      )
    },
    {
      field: "remaining_payment_system",
      headerName: systemCurrency
        ? `Remaining (${systemCurrency})`
        : "Remaining (System)",
      flex: 1.1,
      minWidth: 180,
      sortable: false,
      renderCell: (params) => (
        <div className="cell-wrap amount-cell" style={{ fontWeight: 500 }}>
          {formatNumberRO(params.row.remaining_payment_system)}
        </div>
      )
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      minWidth: 170,
      renderCell: (params) => {
        const value = params.value || "";
        return (
          <div
            className={`invoice-status-badge ${value === "Compliant invoice"
              ? "compliant-invoice"
              : value === "Invoice issued"
                ? "invoice-issued"
                : ""
              }`}
          >
            {value}
          </div>
        );
      }
    },
    {
      field: "invoice_due_date",
      headerName: "Invoice Due Date",
      flex: 1,
      minWidth: 140,
      renderCell: (params) => (
        <div className="cell-wrap">{formatDate(params.value)}</div>
      )
    },
    {
      field: "receipt_due_date",
      headerName: "Invoice Payment Date",
      flex: 1,
      minWidth: 140,
      renderCell: (params) => (
        <div className="cell-wrap">{formatDate(params.value)}</div>
      )
    },
    {
      field: "description",
      headerName: "Description",
      flex: 1.8,
      minWidth: 250,
      renderCell: (params) => (
        <div className="cell-wrap">{params.value}</div>
      )
    },
  ];

  if (canEdit || canView || canDelete) {
    columns.push({
      field: "action",
      headerName: "Action",
      width: 120,
      renderCell: (params) => (
        <>
          {canEdit && (
            <IconButton
              onClick={() => handleEditClick(params.row)}
              sx={{ color: "#1976d2" }}
            >
              <EditNoteSharpIcon />
            </IconButton>
          )}
          {canView && (
            <IconButton
              onClick={() => handleViewClick(params.row)}
              sx={{ color: "#1976d2" }}
            >
              <VisibilityIcon />
            </IconButton>
          )}
          {canDelete && (
            <IconButton
              onClick={() => handleDeleteClick(params.row)}
              sx={{ color: "#d32f2f" }}
            >
              <DeleteIcon />
            </IconButton>
          )}
        </>
      ),
      sortable: false,
      disableColumnMenu: true,
    });
  }

  return (
    <Routes>
      <Route
        path="/"
        element={
          <div className="invoices-register-page">
            <div className="invoices-register-container">
              <h2 className="title">INVOICES REGISTER</h2>

              {canCreate && (
                <div className="add-button-container">
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={handleAddClick}
                    disabled={!canCreate}
                    sx={{ float: "right", marginBottom: "40px" }}
                  >
                    Add Invoice
                  </Button>
                </div>
              )}
            </div>

            <div className="table-container">
              <DataGrid
                id="invoice-register-table"
                rows={rows}
                columns={columns}
                getRowHeight={() => "auto"}
                disableRowSelectionOnClick={true}
                initialState={{
                  sorting: {
                    sortModel: [{ field: "id", sort: "desc" }]
                  },
                  pagination: {
                    paginationModel: {
                      pageSize: 12,
                    },
                  },
                }}
                pageSizeOptions={[10, 12, 20, 25]}
                sx={{
                  "& .MuiDataGrid-cell": {
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    lineHeight: "1.4",
                    display: "flex",
                    alignItems: "center",
                    paddingTop: "4px",
                    paddingBottom: "4px",
                  },
                  "& .MuiDataGrid-columnHeaders": {
                    whiteSpace: "normal",
                    maxHeight: "unset !important",
                    lineHeight: "1.2 !important",
                  },
                  "& .MuiDataGrid-columnHeader": {
                    whiteSpace: "normal",
                    maxHeight: "unset !important",
                    lineHeight: "1.2 !important",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  },
                  "& .MuiDataGrid-columnHeaderTitleContainer": {
                    whiteSpace: "normal",
                    overflow: "visible",
                    lineHeight: "1.2",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                  },
                  "& .MuiDataGrid-columnHeaderTitle": {
                    whiteSpace: "normal",
                    lineHeight: "1.2",
                    textOverflow: "clip",
                    overflow: "visible",
                    wordBreak: "break-word",
                    fontWeight: 700,
                    textAlign: "center",
                    display: "block",
                  },
                  "& .MuiDataGrid-cell:focus": {
                    outline: "none",
                  },
                  "& .MuiDataGrid-cell:focus-within": {
                    outline: "none",
                  },
                  "& .Mui-selected:hover": {
                    backgroundColor: "transparent !important",
                  },
                }}
              />
            </div>

            <InvoiceDeleteDialog
              open={openDeleteDialog}
              record={itemToDelete}
              onClose={() => {
                setOpenDeleteDialog(false);
                setItemToDelete(null);
              }}
              onDeleted={() => {
                setOpenDeleteDialog(false);
                setItemToDelete(null);
                fetchInvoices();
              }}
            />

            <Dialog
              open={errorDialogOpen}
              onClose={() => setErrorDialogOpen(false)}
              aria-labelledby="error-dialog-title"
              aria-describedby="error-dialog-description"
            >
              <DialogTitle id="error-dialog-title" style={{ color: "red" }}>
                Delete Failed
              </DialogTitle>
              <DialogContent>
                <DialogContentText id="error-dialog-description">
                  {deleteErrorMessage}
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={() => setErrorDialogOpen(false)}
                  sx={{
                    backgroundColor: "#e0e0e0",
                    color: "#424242",
                    "&:hover": {
                      backgroundColor: "#bdbdbd",
                    }
                  }}
                >
                  Close
                </Button>
              </DialogActions>
            </Dialog>
          </div>
        }
      />

      <Route path="create" element={canCreate ? <AddInvoice /> : <NotAuthorized />} />

      <Route path="edit/:id" element={canEdit ? <EditInvoice /> : <NotAuthorized />} />

      <Route
        path="details-payment-invoice-commercial/:id"
        element={canView ? <DetailsInvoice /> : <NotAuthorized />}
      />
    </Routes>
  );
}

export default InvoicesRegister;